import React, { useEffect, useMemo } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { useAuth, hasAuthParams } from "react-oidc-context";
import * as Sentry from "@sentry/react";

import "./index.css";
import ErrorBoundary from "./components/errors/ErrorBoundary";
import AuthLoadingOverlay from "./components/AuthLoadingOverlay";
import AuthErrorPage from "./components/AuthErrorPage";

import Home from "./routes/Home";
import Settings from "./routes/Settings";
import InternalDomoReport from "./routes/InternalDomoReport";
import LoggerService from "./services/LoggerService";
import ApiService from "./services/ApiService";

Sentry.init({
  dsn: `${process.env.REACT_APP_SENTRY_DSN}`,
  tunnel: `${process.env.REACT_APP_LAMBDA_PROXY_ENDPOINT}`,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    "localhost",
    "*.fluentco.com",
    "*.minionplatform.com",
  ],
});

function App() {
  const sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouter(createBrowserRouter);

  const auth = useAuth();

  // Extract idpHint from URL params and set cookie if internal user
  const getIdpHintFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("idpHint");
  };

  const idpHint = getIdpHintFromUrl();

  // Set isInternalUser cookie if this is an internal login flow
  React.useEffect(() => {
    if (idpHint === "saml-okta") {
      document.cookie =
        "isInternalUser=true; path=/; SameSite=None; Secure";
    }
  }, [idpHint]);

  // Initialize API service with auth context
  const apiService = useMemo(() => {
    return new ApiService(
      () => auth.user?.access_token,
      () => auth.signoutRedirect()
    );
  }, [auth]);

  // Cleanup API service interceptors when component unmounts or auth changes
  useEffect(() => {
    return () => {
      apiService.cleanup();
    };
  }, [apiService]);

  // Fetch user data
  const getUserData = async () => {
    // If not authenticated yet, let the component handle redirect
    if (!auth?.user?.access_token) {
      throw new Error("Authentication required");
    }

    try {
      const userData = await apiService.getUserProfile();
      
      // If userData is null, token has expired and signoutRedirect was called
      if (userData === null) {
        throw new Error("Token expired");
      }

      return userData;
    } catch (error) {
      LoggerService.sendEvent("user_data_loader_error", "error", {
        error: error.message,
      });
      throw error;
    }
  };

  // Sentry-wrapped router
  const router = sentryCreateBrowserRouter([
    {
      path: "/",
      element: <Home />,
      errorElement: <ErrorBoundary />,
      loader: async () => await getUserData(),
    },
    {
      path: "settings",
      element: <Settings />,
      errorElement: <ErrorBoundary />,
      loader: async () => await getUserData(),
    },
    {
      path: "internal-domo-report",
      element: <InternalDomoReport />,
      errorElement: <ErrorBoundary />,
      loader: async () => await getUserData(),
    },
  ]);

  // Handle token expiration
  useEffect(() => {
    const checkTokenExpiration = () => {
      if (!auth.isAuthenticated || auth.user?.expired) {
        LoggerService.addBreadcrumb(
          "Token has expired - signing out",
          "auth",
          "warning"
        );
        document.cookie =
          "isInternalUser=; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None; Secure";
        auth.signoutRedirect();
      }
    };

    auth.events.addAccessTokenExpired(checkTokenExpiration);

    return () => {
      auth.events.removeAccessTokenExpired(checkTokenExpiration);
    };
  }, [auth]);

  // Redirect to login if not authenticated (unless we're in the callback flow)
  useEffect(() => {
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !hasAuthParams()
    ) {
      LoggerService.addBreadcrumb(
        "Not authenticated - triggering signin redirect",
        "auth",
        "info"
      );
      const signinOptions = idpHint
        ? { extraQueryParams: { kc_idp_hint: idpHint } }
        : {};
      auth.signinRedirect(signinOptions);
    }
  }, [auth.isLoading, auth.isAuthenticated, idpHint]);

  // Handle retry for stuck authentication
  const handleAuthRetry = () => {
    LoggerService.addBreadcrumb(
      "User clicked retry - triggering signin redirect",
      "auth",
      "info"
    );
    const signinOptions = idpHint
      ? { extraQueryParams: { kc_idp_hint: idpHint } }
      : {};
    auth.signinRedirect(signinOptions);
  };

  // Handle retry from error page - clear error and attempt login again
  const handleAuthErrorRetry = () => {
    LoggerService.addBreadcrumb(
      "User clicked retry on error page - clearing state and retrying login",
      "auth",
      "info"
    );
    // Clear stale auth state to allow fresh login attempt
    auth.clearStaleState();
    // Redirect back to login with IdP hint if available
    const signinOptions = idpHint
      ? { extraQueryParams: { kc_idp_hint: idpHint } }
      : {};
    auth.signinRedirect(signinOptions);
  };

  // Handle authentication and app states
  if (auth.isLoading) {
    return <AuthLoadingOverlay message="Loading..." onRetry={handleAuthRetry} />;
  }

  if (auth.error) {
    LoggerService.sendEvent("auth_error", "error", {
      error: auth.error.message,
      userProfile: auth.user?.profile,
    });
    return <AuthErrorPage error={auth.error} onRetry={handleAuthErrorRetry} />;
  }

  // Not yet authenticated
  if (!auth.isAuthenticated) {
    return (
      <AuthLoadingOverlay
        message="Signing you in..."
        onRetry={handleAuthRetry}
      />
    );
  }

  // Authenticated - render the app
  return <RouterProvider router={router} />;
}

export default App;
